"use client";

import type { FC, KeyboardEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import { Checkbox } from "@/components/checkbox";
import { Tag } from "@/components/tag";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "./field-chrome";

type ComboboxOption = {
  value: string;
  label: string;
};

type ComboboxProps = {
  className?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  options: ComboboxOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  id?: string;
};

export const Combobox: FC<ComboboxProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  placeholder = "Please select",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  options,
  value,
  defaultValue,
  onValueChange,
  disabled,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string | undefined>(defaultValue);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const selected = value ?? internal;

  const filtered = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const selectedOption = options.find((option) => option.value === selected);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch("");
      setHighlightedIndex(0);
    }
  };

  const select = (optionValue: string) => {
    setInternal(optionValue);
    onValueChange?.(optionValue);
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const option = filtered[highlightedIndex];
        if (option) {
          event.preventDefault();
          select(option.value);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger
          id={id}
          disabled={disabled}
          className={cn(
            "flex w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-left outline-none focus:border-brand-blue disabled:cursor-not-allowed disabled:bg-neutral-black/5",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
            className,
          )}
        >
          <span className={cn("min-w-0 truncate text-16", selectedOption ? "text-neutral-black" : "text-neutral-black/35")}>
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              (event.currentTarget as HTMLElement).querySelector("input")?.focus();
            }}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-4 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div className="flex items-center gap-8 rounded-small border border-neutral-mid-grey px-12 py-8">
              <MagnifyingGlassIcon className="size-20 shrink-0 text-neutral-black/35" aria-hidden />
              <input
                autoFocus
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
              />
            </div>
            <div className="flex max-h-280 flex-col gap-4 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-8 py-12 text-14 text-neutral-grey">{emptyText}</p>
              ) : (
                filtered.map((option, index) => {
                  const isSelected = option.value === selected;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => select(option.value)}
                      className={cn(
                        "flex cursor-pointer items-center gap-6 rounded-small px-16 py-12 text-left text-16 text-neutral-black outline-none transition-colors duration-100",
                        index === highlightedIndex && "bg-tone-aqua-tint",
                      )}
                    >
                      <span className="flex size-20 shrink-0 items-center justify-center">
                        {isSelected && <CheckIcon className="size-20 text-neutral-black" />}
                      </span>
                      <span className="min-w-0">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};

type MultiComboboxProps = {
  className?: string;
  containerClassName?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  options: ComboboxOption[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (values: string[]) => void;
  maxItems?: number;
  maxVisibleTags?: number;
  disabled?: boolean;
  id?: string;
};

const DEFAULT_MAX_VISIBLE_TAGS = 6;

export const MultiCombobox: FC<MultiComboboxProps> = ({
  className,
  containerClassName,
  label,
  message,
  state = "default",
  required,
  placeholder = "Please select",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  options,
  value,
  defaultValue = [],
  onValueChange,
  maxItems,
  maxVisibleTags = DEFAULT_MAX_VISIBLE_TAGS,
  disabled,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string[]>(defaultValue);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selected = value ?? internal;
  const atCap = maxItems !== undefined && selected.length >= maxItems;

  const filtered = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const selectedOptions = options.filter((option) => selected.includes(option.value));
  const visible = maxVisibleTags > 0 ? selectedOptions.slice(0, maxVisibleTags) : selectedOptions;
  const hidden = selectedOptions.length - visible.length;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch("");
      setHighlightedIndex(0);
    }
  };

  const toggle = (optionValue: string) => {
    const isSelected = selected.includes(optionValue);
    if (!isSelected && atCap) return;

    const next = isSelected ? selected.filter((v) => v !== optionValue) : [...selected, optionValue];
    setInternal(next);
    onValueChange?.(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const option = filtered[highlightedIndex];
        if (option) {
          event.preventDefault();
          toggle(option.value);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-8", containerClassName)}>
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger
          id={id}
          disabled={disabled}
          className={cn(
            "flex min-h-62 w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-left outline-none focus:border-brand-blue",
            "disabled:cursor-not-allowed disabled:bg-neutral-black/5",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
            className,
          )}
        >
          {selected.length === 0 ? (
            <span className="text-16 text-neutral-black/35">{placeholder}</span>
          ) : (
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-8">
              {visible.map((option) => (
                <Tag
                  key={option.value}
                  size="s"
                  className={cn("max-w-full", !disabled && "cursor-pointer")}
                  iconRight={!disabled}
                  onClick={
                    disabled
                      ? undefined
                      : (event) => {
                          event.stopPropagation();
                          toggle(option.value);
                        }
                  }
                >
                  <span className="truncate">{option.label}</span>
                </Tag>
              ))}
              {hidden > 0 && (
                <Tag size="s" iconRight={false} className="text-neutral-black/70">
                  +{hidden}
                </Tag>
              )}
            </span>
          )}
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              (event.currentTarget as HTMLElement).querySelector("input")?.focus();
            }}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-4 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div className="flex items-center gap-8 rounded-small border border-neutral-mid-grey px-12 py-8">
              <MagnifyingGlassIcon className="size-20 shrink-0 text-neutral-black/35" aria-hidden />
              <input
                autoFocus
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
              />
            </div>
            <div className="flex max-h-280 flex-col gap-4 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-8 py-12 text-14 text-neutral-grey">{emptyText}</p>
              ) : (
                filtered.map((option, index) => {
                  const isSelected = selected.includes(option.value);
                  const disabled = !isSelected && atCap;

                  return (
                    <label
                      key={option.value}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={cn(
                        "flex items-center gap-8 rounded-small px-8 py-8 transition-colors duration-100",
                        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                        index === highlightedIndex && "bg-tone-aqua-tint",
                      )}
                    >
                      <Checkbox
                        size="m"
                        checked={isSelected}
                        disabled={disabled}
                        onCheckedChange={() => toggle(option.value)}
                      />
                      <span className="min-w-0 text-16 text-neutral-black">{option.label}</span>
                    </label>
                  );
                })
              )}
            </div>
            {maxItems !== undefined && (
              <p className="px-8 pt-8 text-14 text-neutral-grey">
                {selected.length}/{maxItems} selected
              </p>
            )}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
